import { useEffect, useState, useCallback, useRef } from 'react';
import { socketService } from '../services/socketService';
import type {
  VoteValue,
  Session,
  Participant,
  VoteSummary,
} from '../types/index';

export interface UseSocketReturn {
  socket: ReturnType<typeof socketService.getSocket>;
  connected: boolean;
  socketId: string | null;
  joinSession: (sessionId: string, name: string, facilitatorToken?: string) => void;
  castVote: (sessionId: string, value: VoteValue) => void;
  showVotes: (sessionId: string) => void;
  resetVotes: (sessionId: string) => void;
  reopenVoting: (sessionId: string) => void;
  onSessionState: (callback: (data: {
    state: 'collecting' | 'revealed';
    participants: Participant[];
    expiresAt: number;
    story?: string;
    voteStartTime: number;
  }) => void) => () => void;
  onVotesRevealed: (callback: (data: {
    votes: Array<{ name: string; value: VoteValue }>;
    summary: VoteSummary;
  }) => void) => () => void;
  onVotesReset: (callback: () => void) => () => void;
  onVotingReopened: (callback: () => void) => () => void;
  onSessionExpired: (callback: (reason: string) => void) => () => void;
  onError: (callback: (message: string, code: string) => void) => () => void;
  disconnect: () => void;
}

export function useSocket(): UseSocketReturn {
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState<ReturnType<typeof socketService.getSocket>>(null);
  const [socketId, setSocketId] = useState<string | null>(null);
  const rejoinInfoRef = useRef<{ sessionId: string; name: string; facilitatorToken?: string } | null>(null);
  const previousSocketIdRef = useRef<string | null>(null);
  const connectionStartTimeRef = useRef<number | null>(null);
  const sessionInvalidRef = useRef<boolean>(false); // Track if session is invalid/expired to prevent rejoin attempts
  const lastDisconnectTimeRef = useRef<number | null>(null);
  const isPageVisibleRef = useRef<boolean>(!document.hidden); // Track page visibility

  useEffect(() => {
    // Use a ref to track if this effect has already run to prevent
    // double connections in React StrictMode
    let mounted = true;
    
    // Only connect if socket doesn't exist - this prevents duplicate connections
    // The socket will be created when joinSession is called if needed
    const existingSocket = socketService.getSocket();
    const s = existingSocket || socketService.connect();
    
    if (!mounted) {
      if (!existingSocket) {
        socketService.disconnect();
      }
      return;
    }
    
    setSocket(s);
    previousSocketIdRef.current = s.id || null;
    connectionStartTimeRef.current = Date.now();

    const handleConnect = () => {
      if (mounted) {
        const newSocketId = s.id || null;
        const previousSocketId = previousSocketIdRef.current;
        const wasReconnection = previousSocketId !== null && previousSocketId !== newSocketId;
        
        setConnected(true);
        setSocketId(newSocketId);
        
        // Only rejoin if session is still valid, socket ID changed, and page is visible
        if (rejoinInfoRef.current && newSocketId && newSocketId !== previousSocketId && !sessionInvalidRef.current && isPageVisibleRef.current) {
          // Only log reconnection in development or if it's been a while
          const timeSinceDisconnect = lastDisconnectTimeRef.current 
            ? Date.now() - lastDisconnectTimeRef.current 
            : null;
          if (process.env.NODE_ENV === 'development' || (timeSinceDisconnect && timeSinceDisconnect > 60000)) {
            console.log('[useSocket] Reconnecting to session:', rejoinInfoRef.current.sessionId);
          }
          socketService.emit('join_session', { 
            sessionId: rejoinInfoRef.current.sessionId, 
            name: rejoinInfoRef.current.name 
          });
        }
        
        previousSocketIdRef.current = newSocketId;
        connectionStartTimeRef.current = Date.now();
        lastDisconnectTimeRef.current = null;
      }
    };

    const handleDisconnect = (reason: string) => {
      if (mounted) {
        const connectionDuration = connectionStartTimeRef.current 
          ? Date.now() - connectionStartTimeRef.current 
          : null;
        
        lastDisconnectTimeRef.current = Date.now();
        
        // Only log disconnections in development or if connection was long-lived (>5 minutes)
        // This reduces noise from routine reconnections
        if (process.env.NODE_ENV === 'development' || (connectionDuration && connectionDuration > 300000)) {
          console.log('[useSocket] Socket disconnected:', {
            reason: reason || 'unknown',
            duration: connectionDuration ? `${Math.round(connectionDuration / 1000)}s` : 'unknown',
          });
        }
        setConnected(false);
        // Don't clear socketId here - keep it so we can detect reconnection
      }
    };

    // Only add listeners if they're not already added
    // Check if socket already has listeners to avoid duplicates
    s.on('connect', handleConnect);
    s.on('disconnect', handleDisconnect);

    // Set initial state if already connected
    if (s.connected) {
      setConnected(true);
      const currentId = s.id || null;
      setSocketId(currentId);
      previousSocketIdRef.current = currentId;
      connectionStartTimeRef.current = Date.now();
    }

    // Track page visibility to reduce reconnection attempts when tab is inactive
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      s.off('connect', handleConnect);
      s.off('disconnect', handleDisconnect);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Don't disconnect here - let SessionPage handle cleanup
      // This prevents disconnection during React StrictMode double-mount
    };
  }, []);

  const joinSession = useCallback(
    (sessionId: string, name: string, facilitatorToken?: string) => {
      // Reset invalid session flag when explicitly joining
      sessionInvalidRef.current = false;
      
      // Store rejoin info for reconnection handling
      rejoinInfoRef.current = { sessionId, name, facilitatorToken };

      // Get existing socket or create new one with facilitatorToken if provided
      let s = socketService.getSocket();
      
      // If no socket exists, create one with facilitatorToken if available
      if (!s) {
        s = socketService.connect(facilitatorToken);
        setSocket(s);
      } else if (!s.connected) {
        // If socket exists but isn't connected, reconnect with facilitatorToken if available
        s = socketService.connect(facilitatorToken);
        setSocket(s);
      } else {
        // Socket exists and is connected - connect() will handle checking if we need to reconnect
        // based on facilitatorToken changes
        s = socketService.connect(facilitatorToken);
        setSocket(s);
      }
      
      // Ensure we're connected before joining
      if (s.connected) {
        socketService.emit('join_session', { sessionId, name });
      } else {
        // Wait for connection before joining
        const handleConnect = () => {
          socketService.emit('join_session', { sessionId, name });
          s.off('connect', handleConnect);
        };
        s.on('connect', handleConnect);
      }
    },
    [],
  );

  const castVote = useCallback((sessionId: string, value: VoteValue) => {
    const s = socketService.getSocket();
    if (!s || !s.connected) {
      console.error('[useSocket.castVote] Socket not connected:', {
        sessionId,
        value,
        socketExists: !!s,
        isConnected: s?.connected || false,
      });
    }
    socketService.emit('cast_vote', { sessionId, value });
  }, []);

  const showVotes = useCallback(
    (sessionId: string) => {
      socketService.emit('show_votes', { sessionId });
    },
    [],
  );

  const resetVotes = useCallback(
    (sessionId: string) => {
      socketService.emit('reset_votes', { sessionId });
    },
    [],
  );

  const reopenVoting = useCallback(
    (sessionId: string) => {
      socketService.emit('reopen_voting', { sessionId });
    },
    [],
  );

  const onSessionState = useCallback(
    (callback: (data: {
      state: 'collecting' | 'revealed';
      participants: Participant[];
      expiresAt: number;
      story?: string;
      voteStartTime: number;
    }) => void) => {
      socketService.on('session_state', callback);
      return () => {
        socketService.off('session_state', callback);
      };
    },
    [],
  );

  const onVotesRevealed = useCallback(
    (callback: (data: {
      votes: Array<{ name: string; value: VoteValue }>;
      summary: VoteSummary;
    }) => void) => {
      socketService.on('votes_revealed', callback);
      return () => {
        socketService.off('votes_revealed', callback);
      };
    },
    [],
  );

  const onVotesReset = useCallback((callback: () => void) => {
    socketService.on('votes_reset', callback);
    return () => {
      socketService.off('votes_reset', callback);
    };
  }, []);

  const onVotingReopened = useCallback((callback: () => void) => {
    socketService.on('voting_reopened', callback);
    return () => {
      socketService.off('voting_reopened', callback);
    };
  }, []);

  const onSessionExpired = useCallback((callback: (reason: string) => void) => {
    socketService.on('session_expired', (data) => callback(data.reason));
    return () => {
      socketService.off('session_expired');
    };
  }, []);

  const onError = useCallback(
    (callback: (message: string, code: string) => void) => {
      socketService.on('error', (data) => {
        // Mark session as invalid if it's not found or expired
        // This prevents repeated rejoin attempts
        if (data.code === 'SESSION_NOT_FOUND' || data.code === 'SESSION_EXPIRED') {
          sessionInvalidRef.current = true;
          rejoinInfoRef.current = null; // Clear rejoin info to stop rejoin attempts
        }
        callback(data.message, data.code);
      });
      return () => {
        socketService.off('error');
      };
    },
    [],
  );

  const disconnect = useCallback(() => {
    socketService.disconnect();
  }, []);

  return {
    socket,
    connected,
    socketId,
    joinSession,
    castVote,
    showVotes,
    resetVotes,
    reopenVoting,
    onSessionState,
    onVotesRevealed,
    onVotesReset,
    onVotingReopened,
    onSessionExpired,
    onError,
    disconnect,
  };
}

